import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

interface MessageRow {
  id: string;
  customer_email: string;
  customer_company: string;
  recipient: string;
  sender: string;
  status: string;
  message_type: string;
  parts: number;
  cost: string | null;
  refunded: boolean;
  provider: string | null;
  provider_msg_id: string | null;
  error_message: string;
  created_at: string;
}

const repo = new AdminListRepository<MessageRow>(apiClient, 'messages');

const columns: DataTableColumn<MessageRow>[] = [
  { id: 'created_at', header: 'Created', accessor: (r) => formatDateTime(r.created_at) },
  {
    id: 'customer_email', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.customer_company}
        <span className="block text-xs text-slate-500">{r.customer_email}</span>
      </span>
    ),
  },
  { id: 'recipient', header: 'To', accessor: 'recipient' },
  { id: 'sender', header: 'From', accessor: 'sender' },
  {
    id: 'status', header: 'Status',
    accessor: (r) => (
      <span>
        <StatusBadge value={r.status} />
        {r.error_message && <span className="block text-xs text-red-700">{r.error_message}</span>}
      </span>
    ),
  },
  { id: 'parts', header: 'Parts', accessor: 'parts' },
  {
    id: 'cost', header: 'Cost',
    accessor: (r) => {
      if (!r.cost) return '—';
      return r.refunded
        ? <span className="text-slate-500 line-through" title="Charge refunded">${r.cost}</span>
        : `$${r.cost}`;
    },
  },
  {
    id: 'provider', header: 'Provider',
    accessor: (r) => (
      <span>
        {r.provider ?? '—'}
        {r.provider_msg_id && (
          <span className="block max-w-[9rem] truncate font-mono text-xs text-slate-500" title={r.provider_msg_id}>
            {r.provider_msg_id}
          </span>
        )}
      </span>
    ),
  },
];

export function MessagesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [search, statusFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Messages</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search recipient, customer, provider id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="queued">Queued</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <DataTable<MessageRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No messages yet.</span>}
      />
    </section>
  );
}
